import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { User as UserIcon, ArrowUpRight } from "lucide-react";
import { CLASS_TEXT, fmtNumber, slaSemaphoreClass } from "./PerfSharedUI";

const RANK_STYLE = [
  "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))]",       // #1 gold
  "bg-muted text-foreground",       // #2 silver
  "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))]",     // #3 bronze
];

function scoreColor(total) {
  if (total >= 90) return "#059669";
  if (total >= 80) return "#65B37B";
  if (total >= 70) return "#F59E0B";
  if (total >= 60) return "#F97316";
  return "#DC2626";
}

/**
 * Per-operator ranking (sortable, defaults to score desc). Each row is
 * a link to /performance/operators/:id. Header carries a "Compare"
 * button that pre-selects the top-2.
 */
export default function PerOperatorTable({ rows, showScore = true }) {
  const [sortBy, setSortBy] = useState(showScore ? "score" : "leads_received");
  const [dir, setDir] = useState("desc");

  const sorted = useMemo(() => {
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      let av; let bv;
      if (sortBy === "score") { av = a.score?.total ?? 0; bv = b.score?.total ?? 0; }
      else if (sortBy === "compliance_pct") { av = a.sla.compliance_pct; bv = b.sla.compliance_pct; }
      else if (sortBy.includes("_to_") || sortBy === "lead_to_deal") { av = a.conversions[sortBy]; bv = b.conversions[sortBy]; }
      else { av = a.counts[sortBy]; bv = b.counts[sortBy]; }
      return (dir === "desc" ? -1 : 1) * ((av ?? 0) - (bv ?? 0));
    });
    return arr;
  }, [rows, sortBy, dir]);

  const setSort = (k) => {
    if (sortBy === k) setDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortBy(k); setDir("desc"); }
  };

  const th = (k, label) => (
    <button
      onClick={() => setSort(k)}
      className={`text-[10px] uppercase tracking-[0.1em] font-bold ${sortBy === k ? "text-foreground" : "text-muted-foreground"} hover:text-foreground`}
    >
      {label} {sortBy === k && (dir === "desc" ? "↓" : "↑")}
    </button>
  );

  if (!rows || rows.length === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl p-10 text-center text-muted-foreground" data-testid="per-operator-table">
        Sem dados de operadores no período.
      </div>
    );
  }

  const topIds = (rows || []).slice(0, 2).map((r) => r.operator_id).join(",");

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="per-operator-table">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <UserIcon className="w-5 h-5 text-foreground" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              Ranking de operadores
            </div>
            <h3 className="font-display text-2xl">Score de performance 0-100</h3>
          </div>
        </div>
        <Link
          to={`/performance/compare?kind=operator&ids=${topIds}`}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-muted/40 border border-border hover:bg-muted font-semibold"
          data-testid="compare-operators-btn"
        >
          Comparar →
        </Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="w-10">#</th>
              <th className="text-left py-2 px-2">{th("operator_name", "Operador")}</th>
              {showScore && <th className="text-right py-2 px-2">{th("score", "Score")}</th>}
              <th className="text-right py-2 px-2">{th("leads_received", "Leads")}</th>
              <th className="text-right py-2 px-2">{th("compliance_pct", "SLA %")}</th>
              <th className="text-right py-2 px-2">{th("estimates_sent", "Estimativas")}</th>
              <th className="text-right py-2 px-2">{th("rt_scheduled", "RT")}</th>
              <th className="text-right py-2 px-2">{th("estimate_to_rt", "Est→RT")}</th>
              <th className="text-right py-2 px-2">{th("deals_closed", "Negócios")}</th>
              <th className="text-right py-2 px-2">{th("lead_to_deal", "Lead→Neg.")}</th>
              <th className="w-4"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => {
              const s = row.score || {};
              const rankCls = RANK_STYLE[i] || "bg-muted/40 text-muted-foreground";
              const slaCls = slaSemaphoreClass(row.sla.compliance_pct);
              return (
                <tr
                  key={row.operator_id}
                  className="border-b border-border/60 hover:bg-muted/40"
                  data-testid={`operator-row-${row.operator_id}`}
                >
                  <td className="py-3 px-2 text-center">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${rankCls}`}>
                      #{i + 1}
                    </span>
                  </td>
                  <td className="py-3 px-2">
                    <Link
                      to={`/performance/operators/${row.operator_id}`}
                      className="hover:underline"
                      data-testid={`op-link-${row.operator_id}`}
                    >
                      <div className="font-semibold text-foreground">{row.operator_name}</div>
                      {s.blockers?.length > 0 && (
                        <div className="text-[10px] text-[hsl(var(--danger))] mt-0.5">⚠ {s.blockers.length} bloqueio(s)</div>
                      )}
                    </Link>
                  </td>
                  {showScore && (
                    <td className="py-3 px-2 text-right">
                      <span
                        className="inline-flex items-center gap-1.5 font-bold text-lg tabular-nums"
                        style={{ color: scoreColor(s.total ?? 0) }}
                      >
                        {s.total?.toFixed(0) ?? "—"}
                        <span className="text-base">{s.emoji}</span>
                      </span>
                    </td>
                  )}
                  <td className="py-3 px-2 text-right tabular-nums">{fmtNumber(row.counts.leads_received)}</td>
                  <td className="py-3 px-2 text-right">
                    <span className={CLASS_TEXT[slaCls]}>
                      {row.sla.compliance_pct?.toFixed(0)}%
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right tabular-nums">{fmtNumber(row.counts.estimates_sent)}</td>
                  <td className="py-3 px-2 text-right tabular-nums">{fmtNumber(row.counts.rt_scheduled)}</td>
                  <td className="py-3 px-2 text-right tabular-nums">{row.conversions.estimate_to_rt?.toFixed(1)}%</td>
                  <td className="py-3 px-2 text-right tabular-nums font-semibold">{fmtNumber(row.counts.deals_closed)}</td>
                  <td className="py-3 px-2 text-right tabular-nums font-semibold">{row.conversions.lead_to_deal?.toFixed(1)}%</td>
                  <td className="py-3 px-2">
                    <Link to={`/performance/operators/${row.operator_id}`} data-testid={`op-arrow-${row.operator_id}`}>
                      <ArrowUpRight className="w-3 h-3 text-muted-foreground/70 hover:text-foreground" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
